import React from 'react';
import Modal from './Modal';
import { Button } from './Button';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  XCircleIcon,
  InformationCircleIcon 
} from '@heroicons/react/24/outline';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: NotificationType;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  showCancel?: boolean;
}

const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  onClose,
  type,
  title,
  message,
  confirmText = 'OK',
  cancelText = 'Mégse',
  onConfirm,
  showCancel = false
}) => {
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon className="h-12 w-12 text-green-600 dark:text-green-400" />;
      case 'error':
        return <XCircleIcon className="h-12 w-12 text-red-600 dark:text-red-400" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-12 w-12 text-orange-600 dark:text-orange-400" />;
      case 'info':
        return <InformationCircleIcon className="h-12 w-12 text-blue-600 dark:text-blue-400" />;
      default:
        return <InformationCircleIcon className="h-12 w-12 text-gray-600 dark:text-gray-400" />;
    }
  };

  const getButtonVariant = () => {
    switch (type) {
      case 'success':
      case 'info':
        return 'primary';
      case 'error':
        return 'danger';
      case 'warning':
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
    >
      <div className="p-6">
        {/* Icon és üzenet */}
        <div className="flex flex-col items-center text-center space-y-4">
          {getIcon()}
          
          <div className="space-y-2">
            <p className="text-base text-gray-700 dark:text-gray-300">
              {message}
            </p>
          </div>
        </div>

        {/* Gombok */}
        <div className="mt-6 flex justify-center space-x-3">
          {showCancel && (
            <Button
              variant="outline"
              onClick={onClose}
              className="min-w-[100px]"
            >
              {cancelText}
            </Button>
          )}
          <Button
            variant={getButtonVariant()}
            onClick={handleConfirm}
            className="min-w-[100px]"
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default NotificationModal;